import { getWindowOrigin } from "@/desktop/use-window-origin";
import { getWindowDisplayId } from "@/desktop/use-window-role";
import {
  resolvePresentationPoint,
  resolvePresentationRect,
} from "./agent-presentation-contract";
import type {
  AgentPresentation,
  AgentPresentationStep,
} from "./agent-presentation-types";

interface Bounds {
  x: number;
  y: number;
  width: number;
  height: number;
}
export function guidePointInside(
  x: number,
  y: number,
  bounds: Bounds,
): boolean {
  return (
    x >= bounds.x &&
    x <= bounds.x + bounds.width &&
    y >= bounds.y &&
    y <= bounds.y + bounds.height
  );
}
const around = (x: number, y: number): Bounds => ({
  x: x - 18,
  y: y - 18,
  width: 36,
  height: 36,
});

/** Only trusted DOM actions in this window can be observed. Desktop actions in
 * other applications deliberately remain pending for explicit confirmation. */
export function observePresentationGuideAction(
  presentation: AgentPresentation,
  step: AgentPresentationStep,
  onDone: () => void,
): () => void {
  if (!("user_action" in step) || !step.user_action) return () => {};
  const rect =
    step.kind === "highlight" || step.kind === "draw"
      ? resolvePresentationRect(step, presentation)
      : null;
  const point =
    step.kind === "point" ? resolvePresentationPoint(step, presentation) : null;
  const target = rect ?? point;
  const origin = getWindowOrigin();
  const displayId = getWindowDisplayId();
  if (
    !target ||
    !origin.ready ||
    (displayId !== null && target.displayId !== displayId)
  )
    return () => {};
  const bounds = rect ?? around(target.x, target.y);
  const path = step.kind === "draw" ? step.path : undefined;
  const start =
    rect && path?.[0]
      ? around(
          rect.x + path[0].x * rect.width,
          rect.y + path[0].y * rect.height,
        )
      : bounds;
  const last = path?.[path.length - 1];
  const end =
    rect && last
      ? around(rect.x + last.x * rect.width, rect.y + last.y * rect.height)
      : null;
  let down: { x: number; y: number; pointerId: number } | null = null;
  let completed = false;
  const eligible = (event: Event) =>
    event.isTrusted &&
    event.target instanceof Element &&
    !event.target.closest("[data-presentation-guide-controls]");
  const inBounds = (event: MouseEvent, area: Bounds) => {
    const current = getWindowOrigin();
    return (
      current.ready &&
      guidePointInside(
        event.clientX + current.x,
        event.clientY + current.y,
        area,
      )
    );
  };
  const focusedInside = (event: Event) => {
    if (!(event.target instanceof HTMLElement)) return false;
    const current = getWindowOrigin();
    if (!current.ready) return false;
    const box = event.target.getBoundingClientRect();
    return guidePointInside(
      box.left + box.width / 2 + current.x,
      box.top + box.height / 2 + current.y,
      bounds,
    );
  };
  const finish = () => {
    if (!completed) {
      completed = true;
      onDone();
    }
  };
  const click = (event: MouseEvent) => {
    if (
      step.user_action === "click" &&
      eligible(event) &&
      (inBounds(event, bounds) || (event.detail === 0 && focusedInside(event)))
    )
      finish();
  };
  const key = (event: KeyboardEvent) => {
    if (
      step.user_action === "enter" &&
      event.key === "Enter" &&
      !event.isComposing &&
      eligible(event) &&
      focusedInside(event)
    )
      finish();
  };
  const change = (event: Event) => {
    if (
      step.user_action !== "type" ||
      !eligible(event) ||
      !focusedInside(event)
    )
      return;
    if (
      (event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement) &&
      event.target.value.trim()
    )
      finish();
  };
  const pointerDown = (event: PointerEvent) => {
    if (
      step.user_action === "drag" &&
      end &&
      event.button === 0 &&
      eligible(event) &&
      inBounds(event, start)
    ) {
      down = { x: event.clientX, y: event.clientY, pointerId: event.pointerId };
    }
  };
  const pointerUp = (event: PointerEvent) => {
    if (
      down &&
      event.pointerId === down.pointerId &&
      end &&
      eligible(event) &&
      inBounds(event, end) &&
      Math.hypot(event.clientX - down.x, event.clientY - down.y) > 6
    )
      finish();
    down = null;
  };
  const pointerCancel = () => {
    down = null;
  };
  window.addEventListener("click", click, true);
  window.addEventListener("keydown", key, true);
  window.addEventListener("change", change, true);
  window.addEventListener("pointerdown", pointerDown, true);
  window.addEventListener("pointerup", pointerUp, true);
  window.addEventListener("pointercancel", pointerCancel, true);
  return () => {
    window.removeEventListener("click", click, true);
    window.removeEventListener("keydown", key, true);
    window.removeEventListener("change", change, true);
    window.removeEventListener("pointerdown", pointerDown, true);
    window.removeEventListener("pointerup", pointerUp, true);
    window.removeEventListener("pointercancel", pointerCancel, true);
  };
}
